import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock } from 'lucide-react';
import type { StandardPlace, StandardItem } from '../../types/standard';
import { ItineraryIcon, HotelItineraryIcon } from '../icons';
import { useReplacementImage } from '../../hooks/useReplacementImage';

interface PlaceCardProps {
  place: StandardPlace;
  onClose: () => void;
  onEventClick: (event: StandardItem, showIndex: number) => void;
  onCategorySelect: (category: string) => void;
  onTypeSelect: (type: string) => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  selectedDate: string;
  categoryColors: Map<string, string>;
}

export const PlaceCard: React.FC<PlaceCardProps> = ({
  place,
  onClose,
  onEventClick,
  onCategorySelect,
  onTypeSelect,
  config,
  translations,
  selectedDate,
  categoryColors
}) => {
  const { i18n } = useTranslation();

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getTranslatedCategory = (category: string): string => {
    const translation = translations[category];
    if (!translation) return category;
    return translation[i18n.language] || translation.fr || category;
  };

  // Construire l'URL d'itinéraire depuis l'hôtel
  const getHotelItineraryUrl = (place: StandardPlace): string => {
    if (!place.fulladress || !config.fulladress) return '';

    // Encoder les adresses pour l'URL
    const origin = encodeURIComponent(config.fulladress);
    const destination = encodeURIComponent(place.fulladress);

    // Construire l'URL avec les coordonnées de l'hôtel comme origine
    return `https://www.google.com/maps/dir/${origin}/${destination}`;
  };

  return (
    <div 
      className="modal-container place-card-modal"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-2xl relative flex flex-col overflow-hidden"
        style={{ 
          borderRadius: config.border_radius,
          height: 'auto',
          maxHeight: '90vh'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header fixe */}
        <div className="sticky top-0 bg-white border-b z-10">
          <button
            onClick={onClose}
            className="absolute top-0 right-0 w-12 h-12 bg-black bg-opacity-50 text-white hover:bg-opacity-70 transition-opacity flex items-center justify-center"
            style={{ 
              borderTopRightRadius: config.border_radius,
              borderBottomLeftRadius: config.border_radius
            }}
          >
            <X size={20} />
          </button>

          <div className="p-6">
            <h2 className="text-2xl font-bold pr-8">{place.name}</h2>
            {place.fulladress && (
              <p className="text-gray-600 mt-1">{place.fulladress}</p>
            )}
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <div className="p-6 space-y-8">
            {place.items.map((item, itemIndex) => {
              const categoryColor = categoryColors.get(item.category) || config['medium-color'];
              // Utiliser le hook useReplacementImage pour chaque item
              const displayImage = useReplacementImage(item.image);

              return (
                <div key={item.id} className="space-y-4">
                  <button
                    onClick={() => onEventClick(item, itemIndex)}
                    className="flex gap-4 bg-gray-50 rounded-lg overflow-hidden hover:bg-gray-100 transition-colors cursor-pointer w-full text-left"
                  >
                    <div className="w-1/3">
                      <div className="relative pb-[100%]">
                        <img
                          src={displayImage}
                          alt={item.name}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    
                    <div className="flex-1 p-4">
                      <h3 className="text-base md:text-xl font-semibold mb-2 line-clamp-2">
                        {item.name}
                      </h3>
                      
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onCategorySelect(item.category);
                          }}
                          className="px-2 py-1 text-xs md:text-sm rounded text-white hover:opacity-80 transition-opacity"
                          style={{ backgroundColor: categoryColor }}
                        >
                          {getTranslatedCategory(item.category)}
                        </button>
                        
                        {item.type && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onTypeSelect(item.type);
                            }}
                            className="px-2 py-1 text-xs md:text-sm rounded text-white opacity-70 hover:opacity-80 transition-opacity"
                            style={{ backgroundColor: config['medium-color'] }}
                          >
                            {getTranslatedCategory(item.type)}
                          </button>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer avec boutons d'action */}
        <div className="sticky bottom-0 bg-white border-t p-4">
          <div className="grid grid-cols-2 gap-3">
            {place.fulladress && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place.fulladress)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: config['major-color'] }}
              >
                <ItineraryIcon size={18} className="mr-2" />
                Itinéraire
              </a>
            )}

            {/* Ajouter le bouton d'itinéraire depuis l'hôtel */}
            {place.fulladress && config.fulladress && (
              <a
                href={getHotelItineraryUrl(place)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center px-4 py-2 rounded-lg text-white"
                style={{ backgroundColor: config['major-color'] }}
              >
                <HotelItineraryIcon size={18} className="mr-2" />
                Depuis l'hôtel
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};